import { useState, useEffect, useRef, useCallback } from 'react'

const STATUS_COLORS = {
  'New Lead': '#6b8278', 'In Progress': '#e6a817', 'Quote Sent': '#3b82f6',
  'Awaiting Client': '#e6a817', 'Under Referral': '#8b5cf6',
  'No Contact 1': '#f97316', 'No Contact 2': '#ea580c', 'No Contact 3': '#c2410c',
  'Policy Placed': '#1e3d30', 'Not Interested': '#dc3545',
}

function highlight(text, query) {
  if (!text || !query) return text || ''
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: 'rgba(242,101,34,0.2)', color: 'var(--orange)', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  )
}

function getInitials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
}

export function InstantSearch({ clients, coverConfirmations, openClient }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(-1)
  const inputRef = useRef(null)
  const dropRef = useRef(null)
  const containerRef = useRef(null)

  // ── Search logic ────────────────────────────────────────────────────────────
  const results = useCallback(() => {
    const q = query.trim().toLowerCase()
    if (q.length < 1) return { clients: [], policies: [], phones: [] }

    const clientHits = []
    const policyHits = []
    const phoneHits = []
    const seen = new Set()

    ;(clients || []).forEach(c => {
      if (seen.has(c.id)) return
      const name = (c.company_name || '').toLowerCase()
      const contact = (c.contact_name || '').toLowerCase()
      const email = (c.email || '').toLowerCase()
      const partner = (c.referring_partner || '').toLowerCase()
      const broker = (c.assigned_broker || '').toLowerCase()
      const phone = (c.phone || '').replace(/\s/g, '').toLowerCase()
      const qPhone = q.replace(/\s/g, '')

      if (name.includes(q) || contact.includes(q) || email.includes(q) || partner.includes(q) || broker.includes(q)) {
        clientHits.push({ client: c, matchField: name.includes(q) ? 'name' : contact.includes(q) ? 'contact' : email.includes(q) ? 'email' : 'other' })
        seen.add(c.id)
      } else if (phone.includes(qPhone) && qPhone.length >= 3) {
        phoneHits.push({ client: c })
        seen.add(c.id)
      }
    })

    ;(coverConfirmations || []).forEach(cc => {
      const d = cc.cover_data || {}
      const policy = (d.policy_number || '').toLowerCase()
      const insurer = (d.insurer || '').toLowerCase()
      const cover = (d.cover_type || '').toLowerCase()
      if (!policy && !insurer) return
      if (policy.includes(q) || insurer.includes(q) || cover.includes(q)) {
        const client = (clients || []).find(c => c.id === cc.client_id)
        if (client) {
          policyHits.push({
            client,
            policy_number: d.policy_number,
            insurer: d.insurer,
            cover_type: d.cover_type,
          })
        }
      }
    })

    return {
      clients: clientHits.slice(0, 6),
      policies: policyHits.slice(0, 4),
      phones: phoneHits.slice(0, 3),
    }
  }, [query, clients, coverConfirmations])

  const res = results()
  const allItems = [
    ...res.clients.map(r => ({ ...r, _type: 'client' })),
    ...res.policies.map(r => ({ ...r, _type: 'policy' })),
    ...res.phones.map(r => ({ ...r, _type: 'phone' })),
  ]
  const hasResults = allItems.length > 0
  const showDrop = open && query.trim().length > 0

  // ── Keyboard nav ────────────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (!showDrop) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, allItems.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)) }
    if (e.key === 'Enter' && cursor >= 0 && allItems[cursor]) {
      e.preventDefault()
      openClient(allItems[cursor].client)
      setQuery(''); setOpen(false); setCursor(-1)
    }
    if (e.key === 'Escape') { setOpen(false); setCursor(-1); inputRef.current?.blur() }
  }

  // ── Click outside ───────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false); setCursor(-1)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Reset cursor when results change
  useEffect(() => { setCursor(-1) }, [query])

  const select = (client) => {
    openClient(client)
    setQuery(''); setOpen(false); setCursor(-1)
  }

  let itemIndex = -1

  const ResultRow = ({ client, sub, matchText, icon }) => {
    itemIndex++
    const idx = itemIndex
    const isActive = cursor === idx
    return (
      <div
        onMouseDown={() => select(client)}
        onMouseEnter={() => setCursor(idx)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '9px 14px',
          cursor: 'pointer',
          background: isActive ? 'var(--forest-subtle)' : 'transparent',
          borderLeft: isActive ? '3px solid var(--forest)' : '3px solid transparent',
          transition: 'background .1s',
        }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: isActive ? 'var(--forest)' : 'var(--surface2)',
          border: '1.5px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, fontWeight: 700,
          color: isActive ? '#fff' : 'var(--forest)',
          flexShrink: 0,
        }}>
          {icon || getInitials(client.company_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {highlight(client.company_name, query)}
          </div>
          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {sub}
          </div>
        </div>
        {client.lead_status && (
          <div style={{
            fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
            background: client.lead_status === 'Policy Placed' ? 'rgba(30,61,48,0.1)' : 'rgba(107,130,120,0.1)',
            color: STATUS_COLORS[client.lead_status] || 'var(--muted)',
            flexShrink: 0, whiteSpace: 'nowrap',
          }}>
            {client.lead_status}
          </div>
        )}
        <div style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0 }}>
          {client.assigned_broker?.split(' ')[0]}
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: 340 }}>
      {/* Input */}
      <div style={{ position: 'relative' }}>
        <span style={{
          position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
          fontSize: 13, color: 'var(--muted)', pointerEvents: 'none',
        }}>🔍</span>
        <input
          ref={inputRef}
          className="form-input"
          style={{ paddingLeft: 34, paddingRight: query ? 32 : 12, fontSize: 13, width: '100%' }}
          placeholder="Search clients, policies, phone..."
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            onMouseDown={e => { e.preventDefault(); setQuery(''); setOpen(false); setCursor(-1); inputRef.current?.focus() }}
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer',
              fontSize: 14, lineHeight: 1, padding: 2,
            }}
          >✕</button>
        )}
      </div>

      {/* Dropdown */}
      {showDrop && (
        <div
          ref={dropRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'var(--surface)',
            border: '1.5px solid var(--border)',
            borderRadius: 12,
            boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
            zIndex: 500,
            overflow: 'hidden',
            maxHeight: 480,
            overflowY: 'auto',
          }}
        >
          {!hasResults ? (
            <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
              No results for "<strong>{query}</strong>"
            </div>
          ) : (
            <>
              {/* Clients / leads section */}
              {res.clients.length > 0 && (
                <>
                  <div style={{
                    padding: '7px 14px 4px',
                    fontSize: 9, fontWeight: 700, color: 'var(--muted)',
                    textTransform: 'uppercase', letterSpacing: '.6px',
                    background: 'var(--surface2)',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    Clients & Leads
                  </div>
                  {res.clients.map((r, i) => (
                    <ResultRow
                      key={`c-${r.client.id}-${i}`}
                      client={r.client}
                      sub={[r.client.contact_name, r.client.phone, r.client.policy_type].filter(Boolean).join(' · ')}
                    />
                  ))}
                </>
              )}

              {/* Policy section */}
              {res.policies.length > 0 && (
                <>
                  <div style={{
                    padding: '7px 14px 4px',
                    fontSize: 9, fontWeight: 700, color: 'var(--muted)',
                    textTransform: 'uppercase', letterSpacing: '.6px',
                    background: 'var(--surface2)',
                    borderBottom: '1px solid var(--border)',
                    borderTop: res.clients.length > 0 ? '1px solid var(--border)' : 'none',
                  }}>
                    Policies
                  </div>
                  {res.policies.map((r, i) => (
                    <ResultRow
                      key={`p-${r.client.id}-${i}`}
                      client={r.client}
                      icon="📋"
                      sub={[r.policy_number, r.insurer, r.cover_type].filter(Boolean).join(' · ')}
                    />
                  ))}
                </>
              )}

              {/* Phone section */}
              {res.phones.length > 0 && (
                <>
                  <div style={{
                    padding: '7px 14px 4px',
                    fontSize: 9, fontWeight: 700, color: 'var(--muted)',
                    textTransform: 'uppercase', letterSpacing: '.6px',
                    background: 'var(--surface2)',
                    borderBottom: '1px solid var(--border)',
                    borderTop: (res.clients.length > 0 || res.policies.length > 0) ? '1px solid var(--border)' : 'none',
                  }}>
                    Phone
                  </div>
                  {res.phones.map((r, i) => (
                    <ResultRow
                      key={`ph-${r.client.id}-${i}`}
                      client={r.client}
                      icon="📞"
                      sub={r.client.phone}
                    />
                  ))}
                </>
              )}

              {/* Footer hint */}
              <div style={{
                padding: '7px 14px',
                fontSize: 10, color: 'var(--muted)',
                background: 'var(--surface2)',
                borderTop: '1px solid var(--border)',
                display: 'flex', gap: 12,
              }}>
                <span>↑↓ navigate</span>
                <span>↵ open</span>
                <span>esc close</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
